import { useEffect, useMemo, useRef, useState } from 'react'
import { Play, Pause, Loader2 } from 'lucide-react'
import type { AudioClip } from '@/lib/types'
import { cn } from '@/lib/utils'
import { AUDIO_SPEEDS, DEFAULT_AUDIO_SPEED, type AudioSpeed } from './InlineAudio'

interface Props {
  /** Audio file URL (Supabase Storage public URL). */
  url: string
  /** Optional clip window — for cards born from an AI split, restrict
   *  playback + the waveform to [start_sec, end_sec] of the source audio. */
  clip?: AudioClip | null
  /** Shared global playback rate + setter (persisted in user prefs). */
  playbackRate?: AudioSpeed
  onPlaybackRateChange?: (rate: AudioSpeed) => void
  className?: string
}

const BAR_COUNT = 80

/** Format seconds as m:ss. */
function fmt(sec: number): string {
  if (!isFinite(sec) || sec < 0) sec = 0
  const m = Math.floor(sec / 60)
  const s = Math.floor(sec % 60)
  return `${m}:${s.toString().padStart(2, '0')}`
}

/** Deterministic fallback bars (used when decoding fails / is pending),
 *  mirroring the design reference's pseudo-random pattern. */
function fallbackPeaks(): number[] {
  return Array.from({ length: BAR_COUNT }, (_, i) => {
    const v = Math.sin(i * 0.7) * Math.cos(i * 0.31) * 0.5 + 0.5
    return Math.max(0.18, Math.min(1, v * 0.9 + ((i * 13) % 7) / 30))
  })
}

/** Media-first waveform audio player. Decodes real amplitude peaks from
 *  the file via the Web Audio API (no external dependency), renders them
 *  as bars, and drives a hidden <audio> element for playback + click-to-
 *  seek. Falls back to a deterministic bar pattern if decoding fails. */
export function WaveformPlayer({
  url,
  clip,
  playbackRate,
  onPlaybackRateChange,
  className,
}: Props) {
  const audioRef = useRef<HTMLAudioElement | null>(null)
  const [peaks, setPeaks] = useState<number[]>(fallbackPeaks)
  const [decoding, setDecoding] = useState(true)
  const [playing, setPlaying] = useState(false)
  const [current, setCurrent] = useState(0)
  const [duration, setDuration] = useState(0)
  const [speedMenu, setSpeedMenu] = useState(false)

  const rate = playbackRate ?? DEFAULT_AUDIO_SPEED

  // Clip window: playback + progress are relative to [start, end] when a
  // clip is set (split children), otherwise the whole file.
  const clipStart = clip?.start_sec ?? 0
  const clipEnd = clip?.end_sec ?? null
  const windowEnd = clipEnd ?? duration
  const windowLen = Math.max(0.01, windowEnd - clipStart)
  const elapsed = Math.max(0, Math.min(windowLen, current - clipStart))
  const progress = windowLen > 0 ? elapsed / windowLen : 0

  // Decode real peaks once per URL.
  useEffect(() => {
    let cancelled = false
    setDecoding(true)
    setPeaks(fallbackPeaks())
    ;(async () => {
      try {
        const res = await fetch(url)
        const buf = await res.arrayBuffer()
        const AC =
          window.AudioContext ||
          (window as unknown as { webkitAudioContext: typeof AudioContext })
            .webkitAudioContext
        const ac = new AC()
        const decoded = await ac.decodeAudioData(buf)
        void ac.close()
        if (cancelled) return
        const raw = decoded.getChannelData(0)
        const block = Math.max(1, Math.floor(raw.length / BAR_COUNT))
        const next: number[] = []
        let max = 0.0001
        for (let i = 0; i < BAR_COUNT; i++) {
          let peak = 0
          for (let j = 0; j < block; j++) {
            const v = Math.abs(raw[i * block + j] ?? 0)
            if (v > peak) peak = v
          }
          next.push(peak)
          if (peak > max) max = peak
        }
        setPeaks(next.map((p) => Math.max(0.12, Math.min(1, p / max))))
      } catch {
        if (!cancelled) setPeaks(fallbackPeaks())
      } finally {
        if (!cancelled) setDecoding(false)
      }
    })()
    return () => {
      cancelled = true
    }
  }, [url])

  // Keep the audio element's rate in sync.
  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = rate
  }, [rate])

  function togglePlay() {
    const a = audioRef.current
    if (!a) return
    if (playing) {
      a.pause()
    } else {
      // Start (or restart) within the clip window.
      if (a.currentTime < clipStart || (clipEnd != null && a.currentTime >= clipEnd)) {
        a.currentTime = clipStart
      }
      a.playbackRate = rate
      void a.play().catch(() => {})
    }
  }

  function onTimeUpdate() {
    const a = audioRef.current
    if (!a) return
    setCurrent(a.currentTime)
    // Stop at the end of the clip window.
    if (clipEnd != null && a.currentTime >= clipEnd) {
      a.pause()
      a.currentTime = clipStart
      setPlaying(false)
    }
  }

  function seekFromEvent(e: React.MouseEvent<HTMLDivElement>) {
    const a = audioRef.current
    if (!a) return
    const rect = e.currentTarget.getBoundingClientRect()
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width))
    a.currentTime = clipStart + ratio * windowLen
    setCurrent(a.currentTime)
  }

  const playedUpTo = useMemo(
    () => Math.round(progress * peaks.length),
    [progress, peaks.length],
  )

  return (
    <div
      className={cn(
        'flex items-center gap-3 rounded-2xl border border-[var(--color-border-soft)] bg-gradient-to-b from-[var(--color-surface)] to-[var(--color-surface-2)] px-4 py-3.5',
        className,
      )}
    >
      <audio
        ref={audioRef}
        src={url}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={onTimeUpdate}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
      />

      {/* Play / pause */}
      <button
        type="button"
        onClick={togglePlay}
        aria-label={playing ? 'Pause' : 'Lire'}
        className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full border-[1.5px] border-[var(--color-accent)] text-[var(--color-accent)] transition-colors hover:bg-[var(--color-accent-bg)]"
      >
        {playing ? (
          <Pause className="h-4 w-4" fill="currentColor" />
        ) : (
          <Play className="h-4 w-4 translate-x-[1px]" fill="currentColor" />
        )}
      </button>

      {/* Waveform — click to seek */}
      <div
        role="slider"
        aria-label="Position de lecture"
        aria-valuemin={0}
        aria-valuemax={Math.round(windowLen)}
        aria-valuenow={Math.round(elapsed)}
        tabIndex={0}
        onClick={seekFromEvent}
        className="flex h-9 min-w-0 flex-1 cursor-pointer items-center gap-[2px]"
      >
        {peaks.map((h, i) => (
          <span
            key={i}
            className={cn(
              'w-[2.5px] shrink-0 rounded-full transition-colors',
              i < playedUpTo
                ? 'bg-[var(--color-accent)]'
                : 'bg-[var(--color-border)]',
            )}
            style={{ height: `${Math.round(h * 100)}%` }}
          />
        ))}
      </div>

      {/* Time + speed */}
      <div className="relative flex shrink-0 flex-col items-end gap-0.5">
        <span className="text-[12.5px] tabular-nums text-[var(--color-text)]">
          {decoding ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin text-[var(--color-text-dim)]" />
          ) : (
            `${fmt(elapsed)} / ${fmt(windowLen)}`
          )}
        </span>
        <button
          type="button"
          onClick={() => setSpeedMenu((v) => !v)}
          className="text-[11px] text-[var(--color-text-dim)] transition-colors hover:text-[var(--color-text)]"
        >
          {rate}×
        </button>
        {speedMenu && (
          <div
            className="absolute right-0 top-full z-10 mt-1 overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] py-1 shadow-[var(--shadow-modal)]"
            onMouseLeave={() => setSpeedMenu(false)}
          >
            {AUDIO_SPEEDS.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => {
                  onPlaybackRateChange?.(s)
                  if (audioRef.current) audioRef.current.playbackRate = s
                  setSpeedMenu(false)
                }}
                className={cn(
                  'block w-full px-4 py-1 text-right text-[12px] transition-colors hover:bg-[var(--color-surface-2)]',
                  s === rate
                    ? 'font-semibold text-[var(--color-accent)]'
                    : 'text-[var(--color-text)]',
                )}
              >
                {s}×
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
